import { IconExternalLink } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";
import { Tag } from "./Tag";
import { SourceLabel } from "./SourceLabel";

// A PubMed record shown next to Juisci publications: the PublicationCard
// grammar (journal, title, authors, design · year) without what a record with
// no Juisci row cannot have (image, AI summary, "New", bookmark, access). Two
// marks tell it apart: the grey "PubMed" pill in place of the journal icon, and
// the external-link glyph in place of the bookmark, since the card opens
// PubMed. The design chip stays slate: type colours belong to Juisci content.
//
// `row` is the desktop shell's line, in the grammar of the desktop Explore row
// (`DesktopArticleRow`): no hover background, the title takes the primary
// colour, and the external-link glyph shows on hover at the end of the
// design · year line instead of standing on a line of its own.
type PubMedPublicationCardProps = {
  layout: "card" | "row";
  title: string;
  journalName: string | null;
  author: string;
  // The publication year, or null when PubMed states none.
  year: number | null;
  // The study design label, or null when PubMed gave no usable type.
  design: string | null;
  url: string;
  className: string | null;
};

function PubMedPublicationCard({
  layout,
  title,
  journalName,
  author,
  year,
  design,
  url,
  className,
}: PubMedPublicationCardProps): React.JSX.Element {
  const { t } = useTranslation();
  const sourceLine = (
    <div className="flex items-start gap-2 w-full text-footnote">
      {/* A brand name, never translated. */}
      <span className="shrink-0 mt-0.5 rounded-full bg-ds-black/5 px-2 py-0.5 text-caption leading-none font-semibold text-ds-slate-700">
        PubMed
      </span>
      {journalName === null ? null : (
        <span className="line-clamp-2 min-w-0">
          <SourceLabel
            source={{ kind: "in", name: journalName }}
            onNameClick={null}
          />
        </span>
      )}
    </div>
  );
  const designChip =
    design === null ? null : (
      <Tag
        size="sm"
        variant="slate"
        state="idle"
        icon={null}
        className="shrink-0"
      >
        {design}
      </Tag>
    );
  const yearLabel =
    year === null ? null : (
      <span className="font-normal whitespace-nowrap">
        {design === null ? String(year) : `· ${String(year)}`}
      </span>
    );

  if (layout === "row")
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          "group flex w-full flex-col gap-1.5 font-dm-sans text-left no-underline",
          className
        )}
      >
        {sourceLine}
        <p className="m-0 text-heading-xs font-semibold text-ds-black transition-colors group-hover:text-ds-primary">
          {title}
        </p>
        {author === "" ? null : (
          <span className="text-footnote text-ds-slate-600">{author}</span>
        )}
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-1 text-caption text-ds-slate-600">
            {designChip}
            {yearLabel}
          </div>
          <span className="inline-flex size-7 shrink-0 items-center justify-center text-ds-slate-700 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
            <IconExternalLink
              stroke={1.5}
              className="size-5"
              aria-hidden="true"
            />
            <span className="sr-only">{t("Opens on PubMed")}</span>
          </span>
        </div>
      </a>
    );

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "bg-white flex flex-col items-start w-full gap-2 font-dm-sans text-left",
        className
      )}
    >
      {sourceLine}
      <div className="flex flex-col gap-0.5 w-full">
        <p className="m-0 w-full font-semibold text-heading-xs text-ds-black">
          {title}
        </p>
        <div className="flex flex-col gap-1 w-full">
          {author === "" ? null : (
            <span className="text-caption font-normal text-ds-slate-600">
              {author}
            </span>
          )}
          <div className="flex items-center justify-between gap-2 min-h-10 w-full">
            <div className="flex flex-wrap gap-1 items-center text-caption text-ds-slate-600 min-w-0 flex-1">
              {designChip}
              {yearLabel}
            </div>
            <span className="inline-flex items-center justify-center size-10 shrink-0 text-ds-slate-700">
              <IconExternalLink
                stroke={1.5}
                className="size-6"
                aria-hidden="true"
              />
              <span className="sr-only">{t("Opens on PubMed")}</span>
            </span>
          </div>
        </div>
      </div>
    </a>
  );
}

export { PubMedPublicationCard, type PubMedPublicationCardProps };
